const count = (value) =>
  Number.isSafeInteger(value) && value >= 0 ? value : null;
const fraction = (value) =>
  Number.isFinite(value) && value >= 0 && value <= 1 ? value : null;
const aliases = {
  scheduledCents: "scheduled_cents",
  ruledCents: "ruled_cents",
  leavesTotal: "leaves_total",
  leavesRuled: "leaves_ruled",
  leavesPartial: "leaves_partial",
  // D14: o que segue a origem, o que fica sem distribuição e a referência da obra, em valor
  // e em serviços. Ausentes num payload gravado antes da origem existir: viram `null`.
  followsBaseCents: "follows_base_cents",
  undistributedCents: "undistributed_cents",
  referenceCents: "reference_cents",
  leavesFollowsBase: "leaves_follows_base",
  leavesUndistributed: "leaves_undistributed",
  // D24: o desembolso projetado pela Ampli e os serviços com ele, gravados pela geração.
  projectedCents: "projected_cents",
  leavesProjected: "leaves_projected",
};
export const normalizeDisbursementCoverage = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const result = Object.fromEntries(
    Object.entries(aliases).map(([key, alias]) => [
      key,
      count(Object.hasOwn(value, key) ? value[key] : value[alias]),
    ]),
  );
  const orphanRules = Object.hasOwn(value, "orphanRules")
    ? value.orphanRules
    : value.orphan_rules;
  return {
    ...result,
    percentage: fraction(value.percentage),
    distributedPercentage: fraction(
      Object.hasOwn(value, "distributedPercentage")
        ? value.distributedPercentage
        : value.distributed_percentage,
    ),
    projectedPercentage: fraction(
      Object.hasOwn(value, "projectedPercentage")
        ? value.projectedPercentage
        : value.projected_percentage,
    ),
    orphanRules: Array.isArray(orphanRules) ? orphanRules : [],
  };
};

/**
 * A cobertura que as telas apresentam (D24): o desembolso projetado pela Ampli sobre o valor
 * de referência da obra — o que tem regra própria e, na origem orçamento, o que o cliente
 * distribuiu. O que só copia uma origem não conta. A área de trabalho e a Projeção
 * Financeira leem ESTA função, então mostram os mesmos números.
 *
 * Curva gerada antes dos campos projetados: cai na leitura mais conservadora, só as regras
 * sobre a referência (ou sobre o previsto, num payload ainda mais antigo).
 */
export const projectedCoverage = (coverage) => {
  if (!coverage) return null;
  const referenceCents = coverage.referenceCents ?? coverage.scheduledCents ?? null;
  const projectedCents = coverage.projectedCents ?? coverage.ruledCents ?? null;
  const percentage = coverage.projectedPercentage
    ?? (referenceCents > 0 && projectedCents != null ? Math.min(1, projectedCents / referenceCents) : null);
  const leavesProjected = coverage.leavesProjected
    ?? (coverage.leavesRuled != null ? coverage.leavesRuled + (coverage.leavesPartial ?? 0) : null);
  const leavesTotal = coverage.leavesTotal == null
    ? null
    : coverage.leavesTotal + (coverage.leavesUndistributed ?? 0);
  return { percentage, projectedCents, referenceCents, leavesProjected, leavesTotal };
};

/** "12 de 698 serviços têm desembolso projetado pela Ampli." */
export const projectedServicesLabel = (projected) => {
  if (projected?.leavesProjected == null || projected?.leavesTotal == null) {
    return "Contagem de serviços indisponível.";
  }
  const { leavesProjected, leavesTotal } = projected;
  return `${leavesProjected.toLocaleString("pt-BR")} de ${leavesTotal.toLocaleString("pt-BR")} ${
    leavesTotal === 1 ? "serviço" : "serviços"
  } ${leavesProjected === 1 ? "tem" : "têm"} desembolso projetado pela Ampli.`;
};

/**
 * A cobertura que a tela apresenta (D14): o valor com distribuição no tempo sobre a
 * referência da obra. `null` quando o payload não traz a referência (curva gerada antes de a
 * origem existir) — a tela então cai na leitura antiga, a cobertura de regras.
 */
export const hasOriginCoverage = (coverage) =>
  coverage?.referenceCents != null &&
  coverage?.followsBaseCents != null &&
  coverage?.ruledCents != null &&
  coverage?.undistributedCents != null;

const plural = (value, one, many) =>
  `${value.toLocaleString("pt-BR")} ${value === 1 ? one : many}`;

/**
 * A frase de serviços da cobertura. Com a divisão da origem: quantos serviços seguem a
 * origem, quantos têm regra própria (inteira ou parcial) e quantos estão sem distribuição.
 * `followsLabel` diz a que origem eles seguem ("o desembolso do ERP"); sem ele, "a origem".
 * Sem a divisão (payload antigo), a leitura antiga, que fala de regra e de "à vista".
 */
export const coverageServicesLabel = (coverage, { followsLabel = "a origem" } = {}) => {
  if (
    hasOriginCoverage(coverage) &&
    coverage.leavesFollowsBase != null &&
    coverage.leavesRuled != null &&
    coverage.leavesPartial != null &&
    coverage.leavesUndistributed != null
  ) {
    const withRule = coverage.leavesRuled + coverage.leavesPartial;
    return `${plural(coverage.leavesFollowsBase, "serviço segue", "serviços seguem")} ${followsLabel}, ${plural(
      withRule,
      "tem regra própria",
      "têm regra própria",
    )} e ${plural(coverage.leavesUndistributed, "está sem distribuição", "estão sem distribuição")}.`;
  }
  return coverage?.leavesRuled == null || coverage?.leavesTotal == null
    ? "Contagem de serviços indisponível."
    : `${coverage.leavesRuled.toLocaleString(
        "pt-BR",
      )} de ${coverage.leavesTotal.toLocaleString(
        "pt-BR",
      )} serviços já têm regra; o restante segue à vista.`;
};
